import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import { CompositionService } from "../../librettoEditor/services/CompositionService";
import { ScriptLayerStore } from "../../librettoEditor/store/ScriptLayerStore";
import { FocusStore } from "../../state/FocusStore";
import { observer } from "mobx-react-lite";
import { DEFAULT_LAYOUT_PACK_ID } from "../../librettoEditor/layouts/LayoutPacks";
import { ProjectAssetsStore } from "../../state/ProjectAssetsStore";
import { LayoutPacksStore } from "../../state/LayoutPacksStore";

type MulticamStyleOption = "automatic" | "active_speaker_only";
type MulticamCutawaysOption = "occasional" | "frequent";

type AutomaticMulticamSidebarProps = {
    onBack: () => void;
    onSubmit?: (options: { style: MulticamStyleOption; cutaways: MulticamCutawaysOption; layoutPackId: string }) => void;
};

function getLayoutPackStorageKey(projectId: string | null | undefined): string {
    const pid = String(projectId ?? "").trim() || "unknown";
    return `libretto.layoutPack.${pid}`;
}

function loadStoredLayoutPackId(projectId: string | null | undefined): string | null {
    try {
        const raw = window.localStorage.getItem(getLayoutPackStorageKey(projectId));
        const id = String(raw ?? "").trim();
        return id || null;
    } catch {}
    return null;
}

function persistLayoutPackId(projectId: string | null | undefined, packId: string): void {
    try {
        if (!projectId) return;
        window.localStorage.setItem(getLayoutPackStorageKey(projectId), String(packId ?? ""));
    } catch {}
}

const AutomaticMulticamSidebar: React.FC<AutomaticMulticamSidebarProps> = observer(({ onBack, onSubmit }) => {
    const [style, setStyle] = useState<MulticamStyleOption>("automatic");
    const [cutaways, setCutaways] = useState<MulticamCutawaysOption>("occasional");
    const [cameraBySpeakerId, setCameraBySpeakerId] = useState<Record<string, number>>({});
    const projectId = String(ProjectAssetsStore.projectId ?? "").trim() || null;
    const [layoutPackId, setLayoutPackId] = useState<string>(() => {
        const stored = loadStoredLayoutPackId(projectId);
        return stored ?? DEFAULT_LAYOUT_PACK_ID;
    });

    const styleLabel = (value: MulticamStyleOption) =>
        value === "automatic" ? "Automatic" : "Show only active speaker";
    const cutawaysLabel = (value: MulticamCutawaysOption) => (value === "occasional" ? "Occasional" : "Frequent");

    const scriptClip: any = CompositionService.getScriptLayerClip?.();

    const speakers = useMemo(() => {
        const overrides = Array.isArray(ScriptLayerStore.speakerOverrides) ? ScriptLayerStore.speakerOverrides : [];
        const seen = new Set<string>();
        const out: Array<{ key: string; speakerId?: string; name: string; sourceIndex?: number }> = [];
        for (const o of overrides) {
            const speakerId = typeof (o as any)?.speakerId === "string" ? String((o as any).speakerId) : undefined;
            const key = speakerId || String((o as any)?.id ?? "");
            if (!key || seen.has(key)) continue;
            seen.add(key);
            out.push({
                key,
                speakerId,
                name: String((o as any)?.speakerName ?? "").trim() || "New Speaker",
                sourceIndex: Number.isFinite((o as any)?.sourceIndex) ? Number((o as any).sourceIndex) : undefined,
            });
        }
        return out;
    }, [ScriptLayerStore.speakerOverridesVersion]);

    const cameras = useMemo(() => {
        if (!scriptClip || typeof scriptClip.getVideoEntries !== "function") return [];
        const entries: any[] = scriptClip.getVideoEntries() ?? [];
        const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
        return entries.map((entry, idx) => {
            const input = entry?.input;
            const cfg = input ? cfgs.find((c: any) => c?.input === input) : undefined;
            const trackId = String(cfg?.assetId ?? "");
            const label = String(cfg?.name ?? scriptClip.names?.[idx] ?? `Camera ${idx + 1}`);
            const faces = trackId ? FocusStore.getMedianNumFaces(trackId) : null;
            return {
                sourceIndex: idx,
                label,
                trackId: trackId || null,
                medianFaces: faces,
            };
        });
    }, [scriptClip, FocusStore.version]);

    const defaultCameraForSpeaker = (speaker: { speakerId?: string; sourceIndex?: number }): number | null => {
        if (!scriptClip) return null;
        const videoEntries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
        const audioEntries: any[] = typeof scriptClip.getAudioEntries === "function" ? scriptClip.getAudioEntries() : [];
        const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
        const setup: any = (scriptClip as any).multicamCameraSetup;

        // 1) Persisted setup: find camera assigned to this speakerId.
        if (setup?.version === 1 && Array.isArray(setup.assignments) && speaker.speakerId) {
            const match = setup.assignments.find(
                (a: any) => a?.kind === "speaker" && String(a?.speakerId ?? "") === String(speaker.speakerId),
            );
            const idx = Number(match?.sourceIndex);
            if (Number.isFinite(idx) && idx >= 0 && idx < videoEntries.length) return idx;
        }

        // 2) Default: match by input (audio source -> same input video source).
        if (speaker.sourceIndex != null && audioEntries[speaker.sourceIndex]?.input) {
            const input = audioEntries[speaker.sourceIndex].input;
            const vIdx = videoEntries.findIndex((v: any) => v?.input === input);
            if (vIdx >= 0) {
                const entry = videoEntries[vIdx];
                const cfg = cfgs.find((c: any) => c?.input === entry?.input);
                const trackId = String(cfg?.assetId ?? "");
                const faces = trackId ? FocusStore.getMedianNumFaces(trackId) : null;
                // Prefer not to default a multi-face camera to a speaker unless no other choice.
                if (faces == null || faces <= 1) return vIdx;
            }
        }

        // 3) Fallback: first non-shared camera if possible.
        for (const cam of cameras) {
            if (cam.medianFaces != null && cam.medianFaces > 1) continue;
            return cam.sourceIndex;
        }
        return videoEntries.length > 0 ? 0 : null;
    };

    // Initialize camera selections for speakers (once speakers/cameras are known).
    useEffect(() => {
        if (!speakers.length || !cameras.length) return;
        setCameraBySpeakerId((prev) => {
            const next: Record<string, number> = { ...prev };
            let changed = false;
            for (const sp of speakers) {
                if (next[sp.key] != null && Number.isFinite(next[sp.key])) continue;
                const def = defaultCameraForSpeaker(sp);
                if (def != null) {
                    next[sp.key] = def;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [speakers.length, cameras.length]);

    // Persist camera setup into the script clip whenever mapping changes.
    useEffect(() => {
        if (!scriptClip || cameras.length === 0) return;
        const speakerIdByKey = new Map<string, string>();
        for (const sp of speakers) {
            if (sp.speakerId) speakerIdByKey.set(sp.key, sp.speakerId);
        }

        const selectedByCamera = new Map<number, string>();
        for (const [speakerKey, camIdxRaw] of Object.entries(cameraBySpeakerId)) {
            const camIdx = Number(camIdxRaw);
            if (!Number.isFinite(camIdx) || camIdx < 0) continue;
            const speakerId = speakerIdByKey.get(speakerKey);
            if (!speakerId) continue;
            selectedByCamera.set(camIdx, speakerId);
        }

        const assignments: any[] = [];
        for (const cam of cameras) {
            const speakerId = selectedByCamera.get(cam.sourceIndex);
            if (speakerId) {
                assignments.push({ sourceIndex: cam.sourceIndex, kind: "speaker", speakerId });
                continue;
            }
            const faces = cam.medianFaces;
            if (faces != null && faces > 1) {
                assignments.push({ sourceIndex: cam.sourceIndex, kind: "shared" });
            } else {
                assignments.push({ sourceIndex: cam.sourceIndex, kind: "unassigned" });
            }
        }

        const nextSetup = { version: 1, assignments };
        try {
            const prevSetup = (scriptClip as any).multicamCameraSetup;
            const a = JSON.stringify(prevSetup ?? null);
            const b = JSON.stringify(nextSetup);
            if (a !== b) {
                (scriptClip as any).multicamCameraSetup = nextSetup;
                CompositionService.notifyClipUpdated(scriptClip);
            }
        } catch {}
    }, [cameraBySpeakerId, cameras, scriptClip, speakers]);

    // Keep pack selection in sync with the per-project layout pack selector (Properties sidebar).
    useEffect(() => {
        const stored = loadStoredLayoutPackId(projectId);
        if (stored && stored !== layoutPackId) {
            setLayoutPackId(stored);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => {
        persistLayoutPackId(projectId, layoutPackId);
    }, [projectId, layoutPackId]);

    // Once packs have loaded from Firestore, clamp any unknown stored pack id to the default.
    useEffect(() => {
        if (!LayoutPacksStore.hydrated) return;
        if (!layoutPackId) {
            setLayoutPackId(DEFAULT_LAYOUT_PACK_ID);
            return;
        }
        if (!LayoutPacksStore.hasPack(layoutPackId)) {
            setLayoutPackId(DEFAULT_LAYOUT_PACK_ID);
        }
    }, [LayoutPacksStore.hydrated, LayoutPacksStore.packs.length, layoutPackId]);

    const rowSx = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
    } as const;

    const labelSx = {
        fontSize: 13,
        fontWeight: 500,
        color: "#1F2937",
    } as const;

    const selectSx = {
        width: 210,
        "& .MuiSelect-select": {
            fontSize: 12,
            fontWeight: 500,
            color: "#1F2937",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            "& fieldset": {
                borderColor: "#E5E7EB",
            },
            "&:hover fieldset": {
                borderColor: "#D1D5DB",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#2B6BFD",
            },
        },
    } as const;

    const handleSubmit = () => {
        onSubmit?.({ style, cutaways, layoutPackId });
    };

    const renderOption = (isSelected: boolean, label: string) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 18, display: "grid", placeItems: "center" }}>
                {isSelected ? <CheckRoundedIcon sx={{ fontSize: 18, color: "#111827" }} /> : null}
            </Box>
            <Typography sx={{ fontSize: 14, color: "#111827" }}>{label}</Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
                    Automatic multicam
                </Typography>
            </Box>

            <Typography sx={{ fontSize: 13, color: "#6B7280", marginLeft: "8px", marginRight: "8px" }}>
                Automatically pick layouts & cameras to show active conversations
            </Typography>

            <Box
                sx={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                }}
            >
                {/* Camera setup */}
                {speakers.length > 0 && cameras.length > 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827", px: 0.5 }}>
                            Camera setup
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 26px 1fr",
                                gap: 1,
                                px: 0.5,
                                alignItems: "center",
                            }}
                        >
                            <Typography sx={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>
                                Speaker
                            </Typography>
                            <Box />
                            <Typography sx={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>
                                Camera
                            </Typography>
                        </Box>

                        {speakers.map((sp) => (
                            <Box
                                key={sp.key}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 26px 1fr",
                                    gap: 1,
                                    px: 0.5,
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    title={sp.name}
                                    sx={{
                                        fontSize: 13,
                                        color: "#111827",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {sp.name}
                                </Typography>
                                <Box sx={{ display: "grid", placeItems: "center" }}>
                                    <ArrowRightRoundedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                                </Box>
                                <TextField
                                    select
                                    size="small"
                                    value={cameraBySpeakerId[sp.key] ?? ""}
                                    onChange={(e) => {
                                        const idx = Number(e.target.value);
                                        if (!Number.isFinite(idx)) return;
                                        setCameraBySpeakerId((prev) => ({ ...prev, [sp.key]: idx }));
                                    }}
                                    sx={selectSx}
                                    SelectProps={{
                                        renderValue: (selected) => {
                                            const idx = Number(selected);
                                            const cam = cameras.find((c) => c.sourceIndex === idx);
                                            return cam?.label ?? "Select camera";
                                        },
                                        MenuProps: {
                                            PaperProps: {
                                                sx: { borderRadius: "12px", mt: 0.5 },
                                            },
                                        },
                                    }}
                                >
                                    {cameras.map((cam) => (
                                        <MenuItem key={cam.sourceIndex} value={cam.sourceIndex}>
                                            {cam.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        ))}

                        <Typography sx={{ fontSize: 12, color: "#6B7280", px: 0.5, lineHeight: "18px" }}>
                            Speakers are labeled in the composition. Cameras are the video sources in the script clip.
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        ...rowSx,
                        px: 0.5,
                    }}
                >
                    <Typography sx={labelSx}>
                        Layout pack
                    </Typography>
                    <TextField
                        select
                        value={layoutPackId}
                        onChange={(e) => setLayoutPackId(String(e.target.value ?? DEFAULT_LAYOUT_PACK_ID))}
                        size="small"
                        variant="outlined"
                        sx={selectSx}
                        SelectProps={{
                            renderValue: (selected) => {
                                const id = String(selected ?? "");
                                const pack = (LayoutPacksStore.packs ?? []).find((p) => String((p as any)?.id ?? "") === id);
                                return String(pack?.title ?? "Default");
                            },
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        borderRadius: "12px",
                                        mt: 0.5,
                                    },
                                },
                            },
                        }}
                    >
                        {(LayoutPacksStore.packs ?? []).map((pack) => (
                            <MenuItem key={pack.id} value={pack.id}>
                                {pack.title}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box
                    sx={{
                        ...rowSx,
                        px: 0.5,
                    }}
                >
                    <Typography sx={labelSx}>
                        Style
                    </Typography>
                    <TextField
                        select
                        value={style}
                        onChange={(e) => setStyle(e.target.value as MulticamStyleOption)}
                        size="small"
                        variant="outlined"
                        sx={selectSx}
                        SelectProps={{
                            renderValue: (selected) => styleLabel(selected as MulticamStyleOption),
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        borderRadius: "12px",
                                        mt: 0.5,
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="automatic">
                            {renderOption(style === "automatic", "Automatic")}
                        </MenuItem>
                        <MenuItem value="active_speaker_only">
                            {renderOption(style === "active_speaker_only", "Show only active speaker")}
                        </MenuItem>
                    </TextField>
                </Box>

                <Box
                    sx={{
                        ...rowSx,
                        px: 0.5,
                    }}
                >
                    <Typography sx={labelSx}>
                        Cutaways
                    </Typography>
                    <TextField
                        select
                        value={cutaways}
                        onChange={(e) => setCutaways(e.target.value as MulticamCutawaysOption)}
                        size="small"
                        variant="outlined"
                        sx={selectSx}
                        SelectProps={{
                            renderValue: (selected) => cutawaysLabel(selected as MulticamCutawaysOption),
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        borderRadius: "12px",
                                        mt: 0.5,
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="occasional">
                            {renderOption(cutaways === "occasional", "Occasional")}
                        </MenuItem>
                        <MenuItem value="frequent">
                            {renderOption(cutaways === "frequent", "Frequent")}
                        </MenuItem>
                    </TextField>
                </Box>

                <Button
                    fullWidth
                    onClick={handleSubmit}
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        color: "#FFFFFF",
                        backgroundColor: "#1A1A1A",
                        borderRadius: "8px",
                        py: 1.1,
                        "&:hover": {
                            backgroundColor: "#2B2B2B",
                        },
                    }}
                >
                    Submit
                </Button>
            </Box>
        </Box>
    );
});

export default AutomaticMulticamSidebar;
